import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll } from 'framer-motion';
import { 
  ArrowRight, Code, Brain, Zap, Github, Twitter, Linkedin, 
  Mail, MapPin, Phone, ChevronRight, Sparkles, Rocket,
  Star, Clock, Users, Shield, Menu, X
} from 'lucide-react';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const { scrollY } = useScroll();

  useEffect(() => {
    return scrollY.onChange((latest) => {
      setScrolled(latest > 50);
    });
  }, [scrollY]);

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const headerVariants = {
    top: { 
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      height: "4rem",
    },
    scrolled: { 
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      height: "3.5rem",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
  };

  const features = [
    { icon: Code, title: "Smart Automation", description: "Streamline your workflows with intelligent automation solutions." },
    { icon: Brain, title: "Deep Learning", description: "Leverage advanced neural networks for complex problem-solving." },
    { icon: Zap, title: "Real-time Analytics", description: "Get instant insights with our powerful analytics engine." },
    { icon: Star, title: "AI Optimization", description: "Optimize your processes with advanced AI algorithms." },
    { icon: Clock, title: "24/7 Processing", description: "Continuous processing and monitoring of your data." },
    { icon: Shield, title: "Secure Platform", description: "Enterprise-grade security for your sensitive data." }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <motion.header 
        className="fixed top-0 left-0 right-0 z-50 border-b border-gray-100"
        initial="top"
        animate={scrolled ? "scrolled" : "top"}
        variants={headerVariants}
        transition={{ duration: 0.3 }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-full">
            <motion.a 
              href="/" 
              className="flex items-center gap-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.img 
                src="/lioceans-logo.png" 
                alt="LitOceans Logo" 
                className="h-8"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
              />
            </motion.a>

            <nav className="hidden md:flex items-center gap-8">
              {["Features", "About", "Contact"].map((item, i) => (
                <motion.a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="text-gray-600 hover:text-primary transition-colors relative group"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                  whileHover={{ y: -2 }}
                >
                  {item}
                  <motion.span
                    className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary"
                    initial={{ width: "0%" }}
                    whileHover={{ width: "100%" }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.a>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <motion.button 
              className="md:hidden text-gray-600"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isMenuOpen ? "close" : "menu"}
                  initial={{ rotate: 0 }}
                  animate={{ rotate: isMenuOpen ? 90 : 0 }}
                  exit={{ rotate: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {isMenuOpen ? <X /> : <Menu />}
                </motion.div>
              </AnimatePresence>
            </motion.button>

            {/* <motion.button 
              className="hidden md:flex bg-primary text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-primary/90 transition-colors items-center gap-2"
              whileHover={{ scale: 1.05, boxShadow: "0 4px 15px rgba(108, 204, 204, 0.4)" }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              Get Started <Sparkles className="w-4 h-4" />
            </motion.button> */}
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="md:hidden bg-white border-t border-gray-100"
            >
              <div className="container mx-auto px-4 py-4">
                <motion.nav 
                  className="flex flex-col gap-4"
                  variants={container}
                  initial="hidden"
                  animate="show"
                >
                  {["Features", "About", "Contact"].map((item) => (
                    <motion.a
                      key={item}
                      href={`#${item.toLowerCase()}`}
                      className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                      variants={item}
                      onClick={() => setIsMenuOpen(false)}
                      whileHover={{ x: 5 }}
                    >
                      <ChevronRight className="w-4 h-4" />
                      {item}
                    </motion.a>
                  ))}
                </motion.nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      {/* Main Content */}
      <main className="flex-grow pt-16">
        {/* Hero Section with Coming Soon */}
        <section className="relative min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-primary/10 to-white overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.1 }}
            className="absolute inset-0 grid grid-cols-8 grid-rows-8"
          >
            {[...Array(64)].map((_, i) => (
              <motion.div
                key={i}
                className="border border-primary/20"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.01 }}
              />
            ))}
          </motion.div>

          <div className="container mx-auto px-4 relative">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="flex justify-center mb-6"
              >
                <Rocket className="w-16 h-16 text-primary" />
              </motion.div>

              <motion.h1 
                className="text-5xl md:text-7xl font-bold mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                Coming{" "}
                <motion.span 
                  className="text-primary inline-block"
                  animate={{ 
                    rotate: [0, 5, -5, 0],
                    scale: [1, 1.2, 1.2, 1],
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  Soon
                </motion.span>
              </motion.h1>

              <motion.p 
                className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                We're working on something amazing. Our AI-powered platform will revolutionize 
                the way you work. Stay tuned!
              </motion.p>

              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center"
                variants={container}
                initial="hidden"
                animate="show"
              >
                <motion.button 
                  className="bg-primary text-white px-8 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors flex items-center gap-2 justify-center group"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Join Waitlist
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <ArrowRight className="w-5 h-5" />
                  </motion.div>
                </motion.button>

                <motion.button 
                  className="border border-primary text-primary px-8 py-3 rounded-full font-medium hover:bg-primary/10 transition-colors flex items-center gap-2 justify-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Learn More
                  <ChevronRight className="w-5 h-5" />
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <motion.h2 
              className="text-4xl font-bold text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Coming Features
            </motion.h2>

            <motion.div 
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              variants={container}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
            >
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  className="p-6 rounded-xl bg-gray-50 shadow-sm relative overflow-hidden group"
                  variants={item}
                  onHoverStart={() => setHoveredFeature(index)}
                  onHoverEnd={() => setHoveredFeature(null)}
                  whileHover={{ scale: 1.05 }}
                >
                  <motion.div
                    className="absolute inset-0 bg-primary/5"
                    initial={{ scale: 0 }}
                    animate={{ 
                      scale: hoveredFeature === index ? 1 : 0,
                      opacity: hoveredFeature === index ? 1 : 0
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  <feature.icon className="w-12 h-12 text-primary mb-4 relative z-10" />
                  <h3 className="text-xl font-semibold mb-3 relative z-10">{feature.title}</h3>
                  <p className="text-gray-600 relative z-10">{feature.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-50 border-t border-gray-100">
        <div className="container mx-auto px-4 py-12">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-4 gap-8"
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <motion.div variants={item}>
              <motion.h3 
                className="text-2xl font-bold text-primary mb-4 flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
              >
                <img src="/lioceans-logo.png" alt="LitOceans Logo" className="h-8" />
              </motion.h3>
              <p className="text-gray-600">Empowering the future with AI technology.</p>
              <div className="mt-4 space-y-2">
                <motion.a 
                  href="mailto:admin@litoceans.com"
                  className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <Mail className="w-4 h-4" />
                  admin@litoceans.com
                </motion.a>
                <motion.div 
                  className="flex items-center gap-2 text-gray-600"
                  whileHover={{ x: 5 }}
                >
                  <MapPin className="w-4 h-4" />
                  Chennai, IN
                </motion.div>
              </div>
            </motion.div>

            <motion.div variants={item}>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <motion.a 
                    href="#about" 
                    className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                    whileHover={{ x: 5 }}
                  >
                    <Users className="w-4 h-4" />
                    About
                  </motion.a>
                </li>
                <li>
                  <motion.a 
                    href="mailto:admin@litoceans.com" 
                    className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                    whileHover={{ x: 5 }}
                  >
                    <Rocket className="w-4 h-4" />
                    Careers
                  </motion.a>
                </li>
                <li>
                  <motion.a 
                    href="#contact" 
                    className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                    whileHover={{ x: 5 }}
                  >
                    <Mail className="w-4 h-4" />
                    Contact
                  </motion.a>
                </li>
              </ul>
            </motion.div>

            <motion.div variants={item}>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li>
                  <motion.a 
                    href="https://blog.litoceans.com" 
                    className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                    whileHover={{ x: 5 }}
                  >
                    <ChevronRight className="w-4 h-4" />
                    Blog
                  </motion.a>
                </li>
                <li>
                  <motion.a 
                    href="https://docs.litoceans.com" 
                    className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                    whileHover={{ x: 5 }}
                  >
                    <ChevronRight className="w-4 h-4" />
                    Documentation
                  </motion.a>
                </li>
                <li>
                  <motion.a 
                    href="/privacy" 
                    className="text-gray-600 hover:text-primary transition-colors flex items-center gap-2"
                    whileHover={{ x: 5 }}
                  >
                    <Shield className="w-4 h-4" />
                    Privacy Policy
                  </motion.a>
                </li>
              </ul>
            </motion.div>

            <motion.div variants={item}>
              <h4 className="font-semibold mb-4">Connect</h4>
              <div className="space-y-4">
                <motion.a 
                  href="https://github.com/litoceans"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <Github className="w-5 h-5" />
                  GitHub
                </motion.a>
                <motion.a 
                  href="https://twitter.com/litoceans"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <Twitter className="w-5 h-5" />
                  Twitter
                </motion.a>
                <motion.a 
                  href="https://linkedin.com/company/litoceans"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors"
                  whileHover={{ x: 5 }}
                >
                  <Linkedin className="w-5 h-5" />
                  LinkedIn
                </motion.a>
              </div>
            </motion.div>
          </motion.div>

          <motion.div 
            className="mt-12 pt-8 border-t border-gray-200 text-center text-gray-600"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p>&copy; {new Date().getFullYear()} Litoceans Technologies. All rights reserved.</p>
          </motion.div>
        </div>
      </footer>
    </div>
  );
}

export default App;
